import { getReportFooterText } from '../reportFooter';
import type { PersonLabels } from '@/hooks/usePersonLabels';
export interface ExpiredAssetsItem {
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  squadronOrSection?: string | null;
  expirationDate?: string | null;
  quantity?: number | null;
  individual?: string | null;
}

export interface ExpiredAssetsTemplateOptions {
  generatedAt: Date;
  header?: {
    expiringByDate?: string | null;
  };
  items: ExpiredAssetsItem[];
  personLabels?: PersonLabels;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatNumber = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return sanitizeText(value);
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  }).format(date);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildItemsTable = (items: ExpiredAssetsItem[]): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="7" class="empty-row">
          No assets are currently expired.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item) => `
        <tr>
          <td>${sanitizeText(item.lin)}</td>
          <td>${sanitizeText(item.nsn)}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td>${sanitizeText(item.squadronOrSection)}</td>
          <td>${formatDate(item.expirationDate ?? null)}</td>
          <td class="numeric">${formatNumber(item.quantity ?? null)}</td>
          <td>${sanitizeText(item.individual)}</td>
        </tr>
      `,
    )
    .join('');
};

export const buildExpiredAssetsReportHtml = ({
  generatedAt,
  header,
  items,
  personLabels,
}: ExpiredAssetsTemplateOptions): string => {
  const pl = personLabels ?? { label: 'Individual', labelPlural: 'Individuals', lower: 'individual', lowerPlural: 'individuals', article: 'an' as const };
  const generatedLabel = formatDateTime(generatedAt);
  const expiringByLabel = header?.expiringByDate ? formatDate(header.expiringByDate) : '—';
  const itemsTable = buildItemsTable(items);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Expired Assets Report</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 12px;
      }

      .title {
        font-size: 22px;
        font-weight: 700;
        margin: 0;
        text-align: center;
      }

      .generated {
        margin-top: 4px;
        font-size: 12px;
        color: #5c6f85;
        text-align: right;
      }

      .filters-row {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-top: 12px;
      }

      .filter-block {
        min-width: 240px;
      }

      .filter-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 2px;
      }

      .filter-value {
        font-size: 14px;
        color: #15263c;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 8px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 8px 10px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 8px 10px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 16px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <h1 class="title">Expired Assets Report</h1>
      <div class="filters-row">
        <div class="filter-block">
          <div class="filter-label">Expiring by date</div>
          <div class="filter-value">${expiringByLabel}</div>
        </div>
      </div>
      <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            <th>LIN</th>
            <th>NSN</th>
            <th>Nomenclature</th>
            <th>Squadron / Section</th>
            <th>Expiration Date</th>
            <th>Quantity</th>
            <th>${pl.label}</th>
          </tr>
        </thead>
        <tbody>
          ${itemsTable}
        </tbody>
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
